import React, { useContext, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { subject } from '@casl/ability';
import { useAbility } from '@casl/react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  List,
  ListItem,
  Paper,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import Grid from '@mui/material/Grid';
import DialogTitle from '@mui/material/DialogTitle';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import TabList from '@components/common/TabList';
import { useConfirm } from '@components/common/hooks/useConfirm';
import useEventDetailsQuery from '@components/common/hooks/api/useEventDetailsQuery';
import EventDemographics from '@components/ContentComponents/Events/Demographics/EventDemographics';
import { getSessionName } from '@components/Pages/Events/Sessions/sessionFunctions';
import { axiosRequest } from '@lib/axios';
import { participantNeeds, programAreas } from '@lib/constants';
import { PageContext } from '@lib/contexts/pageContext';
import UserContext from '@lib/contexts/userContext';
import moment from '@lib/moment';
import { AbilityContext, Subjects } from '@lib/permissions';
import { formatDateTime, scrollToAlert } from '@lib/utils';
import { getDemographicsSummary, hasCompleteDemographics } from '../eventFunctions';
import EventSessionsTable from './EventSessionsTable';
import CompleteEventModal from '@components/ContentComponents/Events/CompleteEvent/CompleteEventModal';
import useFormModal from '@components/common/hooks/useFormModal/useFormModal';

const EventDetails = ({ setTitle }) => {
  const queryClient = useQueryClient();

  const [deleting, setDeleting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [currentTab, setCurrentTab] = useState('sessions');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');

  const {
    appendErrorMessages,
    setPageMessage,
    setPageErrorMessage,
    setBreadcrumbValue,
  } = useContext(PageContext);
  const { userRecord, authenticating } = useContext(UserContext);

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const {eventID} = useParams();

  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);
  const confirm = useConfirm();

  const formModal = useFormModal();

  const { data: event, isLoading, isError, error, refetch } = useEventDetailsQuery(eventID);

  useEffect(() => {
    let active = true;
    if (isError && active) {
      console.log('[EventDetails] error fetching event:', error);
      setTitle('Error Accessing Page');
    }
    // The active var helps prevent errors due to state changes on unmounted components (when we redirect)
    return () => {
      active = false;
    }
  }, [error, isError, setTitle]);

  useEffect(() => {
    let active = true;
    if (event && active) {
      setBreadcrumbValue(':event', event);
    }
    if (event) {
      event.sessions?.forEach((session) => {
        session.event = event;
        session.name = getSessionName({
          session,
          showNumbers: false,
          dateFallback: false,
          useNill: true,
        });
      });
    }

    // The active var helps prevent errors due to state changes on unmounted components (when we redirect)
    return () => {
      active = false;
    }
    // eslint-disable-next-line
  }, [event]);

  /**
   * Mutation for deleting an event and all of it associated sessions
   */
  const deleteMutation = useMutation({
    mutationFn: async () => axiosRequest({
      method: 'DELETE',
      url: `/events/${eventID}`,
    }),
    onSuccess: (event) => {
      setPageErrorMessage('');
      navigate('/events', { state: { message: `Event "${event.event_name}" and its sessions have been deleted.` } });
    },
    onError: (err) => {
      setPageErrorMessage('An error occurred while deleting this event. Please contact <support email address> for assistance.', err);
      console.log('[EventDetails][delete] error:', err);
      scrollToAlert();
      // Refetch data, in case some sessions were deleted before the error occurred.
      refetch().then();
    },
    onSettled: () => {
      setDeleting(false);
    },
  });

  /**
   * Mutation for updating events. Used by the status change buttons.
   */
  const updateEventMutation = useMutation({
    mutationFn: async (data) => axiosRequest({
      method: 'PUT',
      url: `/events/${eventID}`,
      data,
    }),
    onSuccess: async () => {
      setPageErrorMessage('');
      refetch();
      await queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: (err) => {
      console.log('[EventDetails][updateEvent] error:', err);
    },
    onSettled: () => {
      setUpdating(false);
    },
  });

  const handleDelete = async () => {

    if (ability.cannot('delete', subject(Subjects.EVENT, event))) {
      setPageErrorMessage('You do not have permission to delete this event.');
      return false;
    }

    const confirmDelete = await confirm({
      title: 'Are you sure you want to delete this event?',
      description: 'If you delete this event, all its sessions will also be deleted. This cannot be undone.',
      confirmationText: 'Delete Event',
      cancellationText: 'Keep Event',
    })
      .then(() => true)
      .catch(() => false);

    if (!confirmDelete) {
      return;
    }
    // TODO: Check to see if any sessions have participants first, if they do, abort.

    setDeleting(true);
    deleteMutation.mutate();
  };

  /**
   * Handler for completing or canceling an event
   */
  const handleComplete = async () => {

    if (ability.cannot('edit', subject(Subjects.EVENT, event))) {
      setDialogTitle('This Event Cannot Be Marked Completed');
      setDialogMessage('You do not have permission to edit this event.');
      setDialogOpen(true);
      return false;
    }

    // Events can't be completed if there are no sessions
    if (!event.sessions?.length) {
      setDialogTitle('This Event Cannot Be Marked Completed');
      setDialogMessage('This event cannot be marked completed because there are no sessions. ' +
        'Please add sessions or delete this event if it is no longer needed.');
      setDialogOpen(true);
      return;
    }

    let newStatus = null;
    let comments = null;
    if (event.sessions?.length && event.sessions?.every(session => session.status === 'CANCELED')) {
      // If all sessions are canceled, show the "Cancel event" dialog.

      newStatus = await confirm({
        title: 'This Event Cannot Be Marked Completed',
        content: <Typography variant="body1">
          This event cannot be marked completed because all sessions are canceled. Please select
          &quot;Cancel Event&quot; if this event has been canceled.
        </Typography>,
        confirmationText: 'Cancel Event',
        cancellationText: 'Close',
      })
        .then(() => 'CANCELED')
        .catch(() => null);
      if (!newStatus) {
        return;
      }
    } else {

      // All sessions must be completed or canceled.
      if (event.sessions?.find(session => session.status !== 'COMPLETED' && session.status !== 'CANCELED')) {
        setDialogTitle('This Event Cannot Be Marked Completed');
        setDialogMessage('This event cannot be marked completed because there are one or more incomplete sessions. ' +
          'To complete an event, please make sure all sessions are marked completed or canceled.');
        setDialogOpen(true);
        return;
      }

      // Demographic counts must be recorded for all Class events.
      if (event.event_type === 'CLASS') {
        let content = null;
        if (!hasCompleteDemographics(event)) {
          content = <>
            <Typography variant="body1">
              This event cannot be marked completed because the demographic counts have not been entered. Click &quot;Edit Demographics&quot; to enter demographics for this event.
            </Typography>
          </>;
        }
        const demoSummary = getDemographicsSummary(event);
        if (!demoSummary.valid) {
          content = <>
            <Typography variant="body1">
              The demographics for this event require your attention. Click &quot;Edit Demographics&quot; to verify the demographic data received.
            </Typography>
          </>
        }

        if (content) {
          await confirm({
            title: 'This Event Cannot Be Marked Completed',
            content,
            confirmationText: 'Edit Demographics',
            cancellationText: 'Close',
          })
            .then(() => navigate(`/events/${eventID}/complete`))
            .catch(() => false);
          return;
        }
      }

      if (newStatus !== 'CANCELED') {
        comments = await formModal({
          title: 'Mark Event Complete',
          Form: CompleteEventModal,
          FormProps: { event },
          confirmationText: 'Yes, Complete Event',
          cancellationText: 'No',
        })
          .then(({ comments }) => comments)
          .catch(() => false);

        if (comments === false) {
          return;
        }
        newStatus = 'COMPLETED';
      }
    }

    const input = {
      status: newStatus,
    };
    if (newStatus === 'COMPLETED') {
      input.completed_by = userRecord.email;
      input.completedAt = moment().toISOString(true);
      input.comments = comments ?? '';
    }
    if (newStatus === 'CANCELED') {
      input.canceled_by = userRecord.email;
      input.canceledAt = moment().toISOString(true);
    }

    setUpdating(true);
    setPageMessage('');
    try {
      await updateEventMutation.mutateAsync(input);
      if (newStatus === 'CANCELED') {
        setPageMessage('Event Canceled.');
      }
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      await refetch();
    } catch (error) {
      setPageErrorMessage('Error changing event status.', error);
      appendErrorMessages(error.errors);
    }
    setUpdating(false);
  };

  /**
   * Handler for undoing the Completed or Canceled status and changing status back to Incomplete
   */
  const handleSetIncomplete = async () => {
    const options = event.status === 'COMPLETED' ?
      {
        title: 'Undo Completed Status',
        content: (
          <Box sx={{ width: '100%' }} key="content">
            <p>When you undo the completed status for this event, it will change from Completed to Incomplete and staff will be able to resume editing.</p>
            <p>Are you sure you want to undo the completed status for this event?</p>
          </Box>
        ),
        confirmationText: 'Yes, Undo Completed Status',
        cancellationText: 'No',
      } : {
        title: 'Undo Canceled Status',
        content: (
          <Box sx={{ width: '100%' }} key="content">
            <p>When you undo the canceled status for this event, it will change from Canceled to Incomplete and staff will be able to resume editing.</p>
            <p>Are you sure you want to undo the canceled status for this event?</p>
          </Box>
        ),
        confirmationText: 'Yes, Undo Canceled Status',
        cancellationText: 'No',
      };

    const shouldChangeStatus = await confirm(options)
      .then(() => true)
      .catch(() => false);

    if (shouldChangeStatus) {
      const input = {
        status: 'INCOMPLETE',
        completed_by: null,
        completedAt: null,
        canceled_by: null,
        canceledAt: null,
      };
      try {
        await updateEventMutation.mutateAsync(input);
        setPageMessage('Event status restored to INCOMPLETE.');
        // If user was on the Demographics tab, it will disappear, so switch them to the Sessions tab.
        setCurrentTab('sessions');
      } catch (error) {
        appendErrorMessages('Error changing event status.');
        appendErrorMessages(error.errors.map(e => e.message));
      }
    }
  };

  const preventAddingSession = () => {
    setDialogTitle('Booth events can only have one session per event');
    setDialogMessage('If you want to schedule another booth, return to the Events page and' +
      ' select "Create Event" then add the session to the new event.');
    setDialogOpen(true);
  }

  const getProgramAreaLabel = (area) => {
    const opt = programAreas.find((pa) => pa.id === area);
    return opt?.label;
  };

  const programAreaLabels = (areas) => {
    if (!areas) {
      return '';
    }
    const areasArray = Array.isArray(areas) ? areas : areas.split(',').map(a => a.trim());
    return areasArray.reduce((acc, area) => {
      const label = getProgramAreaLabel(area);
      if (label) {
        acc.push(label);
      }
      return acc;
    }, []).join(', ');
  }

  const getParticipantNeeds = () => event.participant_needs.map((id) => {
    if (id === 'other') {
      return `Other: ${event.participant_needs_other}`;
    }
    const need = participantNeeds.find((pn) => pn.id === id);
    return need?.label || id;
  });

  // Admins can still edit Completed events. Staff users can't.
  const canEdit = event && ability.can('edit', subject(Subjects.EVENT, event));

  const canAddSession = event && (event.event_type !== 'BOOTH' || !event.sessions.find(s => s.status !== 'CANCELED'));

  const tabs = [
    {
      name: 'sessions',
      label: 'SESSIONS',
    },
  ];
  if (event?.status === 'COMPLETED') {
    tabs.push({
      name: 'demographics',
      label: 'DEMOGRAPHICS',
    });
  }

  return (
    <div id="pagewrapper" className={'with-two-sidebar'} style={{padding: 0}}>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        disableRestoreFocus
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button id="dialog-close" variant="contained" onClick={handleDialogClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <div id="content-wrapper" className={'event-details-card'}>
        { isLoading && (
          <Grid item xs={12} textAlign="center">
            <CircularProgress />
          </Grid>
        )}
        {!isLoading && event && (
          <>
            {event.status === 'COMPLETED' && (
              <Alert severity="success" sx={{ marginBottom: '10px', position: 'relative' }}>
                This event is completed.
                <br/>
                Completed on: { formatDateTime(event.completedAt) } by { event.completed_by_names || 'unknown user' }
                <Button
                  variant="contained"
                  onClick={handleSetIncomplete}
                  disabled={!canEdit}
                  sx={{
                    display: 'inline-block',
                    position: 'absolute',
                    right: '10px',
                    bottom: '10px',
                  }}
                >
                  Undo Completed Status
                </Button>
              </Alert>
            )}

            {event.status === 'CANCELED' && (
              <Alert severity="warning" sx={{ marginBottom: '10px', position: 'relative' }}>
                This event is canceled.
                <br/>
                Canceled on: { formatDateTime(event.canceledAt) } by { event.canceled_by_names || 'unknown user' }
                {canEdit && (
                  <Button
                    variant="contained"
                    onClick={handleSetIncomplete}
                    disabled={!canEdit}
                    sx={{
                      display: 'inline-block',
                      position: 'absolute',
                      right: '10px',
                      bottom: '10px',
                    }}
                  >
                    Undo Canceled Status
                  </Button>
                )}
              </Alert>
            )}

            <Paper elevation={3}>
              <Box padding={3}>
                <Typography variant="h1" color="ilstorm.main">
                  {event.event_name}
                </Typography>
                {event.is_test && (
                  <Chip label="Test Event"/>
                )}
                <List dense={true}>
                  <ListItem disablePadding>
                    Program Area(s): {programAreaLabels(event.program_areas)}
                  </ListItem>
                  {event.curriculum && (
                    <ListItem disablePadding>
                      Primary Curriculum: {event?.curriculum?.name}
                    </ListItem>
                  )}
                  {event?.curriculum?._count?.additionals > 0 && (
                    <ListItem disablePadding>
                      Additional Curriculum: {event?.additional?.name || 'None'}
                    </ListItem>
                  )}
                  <ListItem disablePadding>
                    Partner Site: {event.event_partner}
                  </ListItem>
                  {event.participant_needs?.length > 0 && (
                    <ListItem disablePadding>
                      Specific Needs: {getParticipantNeeds().join(', ')}
                    </ListItem>
                  )}
                  {event?.special_project && (
                    <ListItem disablePadding>
                      Special Project: {event.special_project}
                    </ListItem>
                  )}
                  <ListItem disablePadding>
                    Created By: {event.created_by_names}
                  </ListItem>
                  <ListItem disablePadding>
                    Last Modified: {`${event.updated_by_names}, ${formatDateTime(event.updatedAt)}`}
                  </ListItem>
                  {event?.status === 'COMPLETED' && (
                    <ListItem disablePadding sx={{ marginTop: '12px' }}>
                      Comments: {event.comments || 'No comments provided.'}
                    </ListItem>
                  )}
                </List>
                <Stack direction="row" justifyContent="space-between" sx={{marginTop: '20px'}}>
                  <Button
                    id="edit-event-button"
                    variant="contained"
                    startIcon={<EditIcon/>}
                    component={RouterLink}
                    to={canEdit ? `/events/edit/${eventID}` : 'javascript:void(0);'}
                    disabled={!canEdit}
                  >
                    Edit Event
                  </Button>
                  <div>
                    <Button
                      id="delete-event-button"
                      variant="contained"
                      color="error"
                      startIcon={<ClearIcon/>}
                      disabled={deleting || !canEdit}
                      onClick={handleDelete}
                    >
                      Delete Event
                    </Button>
                    {event.status !== 'COMPLETED' && event.status !== 'CANCELED' && (
                      <Button
                        id="complete-event-button"
                        variant="contained"
                        color="success"
                        startIcon={<CheckIcon/>}
                        disabled={updating || !canEdit}
                        onClick={handleComplete}
                        sx={{marginLeft: '20px'}}
                      >
                        Complete Event
                      </Button>
                    )}
                  </div>
                </Stack>
              </Box>
            </Paper>

            <TabList tabs={tabs} currentTab={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)} />

            {currentTab === 'sessions' && (
              <Box mt="28px" role={tabs.length > 1 ? 'tabpanel' : ''}>
                {!canEdit && (
                  <Button
                    id="add-session-button"
                    variant="contained"
                    startIcon={<AddIcon/>}
                    onClick={() => {}}
                    disabled={true}
                  >
                    Add Session
                  </Button>
                )}
                {canEdit && canAddSession && (
                  <Button
                    id="add-session-button"
                    variant="contained"
                    startIcon={<AddIcon/>}
                    component={RouterLink}
                    to={`/events/${eventID}/sessions/add`}
                  >
                    Add Session
                  </Button>
                )}
                {canEdit && !canAddSession && (
                  <Button
                    id="add-session-button"
                    variant="contained"
                    startIcon={<AddIcon/>}
                    onClick={preventAddingSession}
                  >
                    Add Session
                  </Button>
                )}
                <EventSessionsTable event={event} sessions={event.sessions} />
              </Box>
            )}
            {currentTab === 'demographics' && (
              <Box mt="28px" role={tabs.length > 1 ? 'tabpanel' : ''}>
                <EventDemographics event={event} showEdit={false} />
              </Box>
            )}
          </>
        )}
        { (!authenticating && !isLoading && !event) && (
          <>
            You do not have access, or the event was not found.
          </>
        )}
      </div>
    </div>
  );
};

export default EventDetails;
